import type { HttpTransport, TransportResponse } from './transport/types'

// ---------------------------------------------------------------------------
// Path param extraction helpers
// ---------------------------------------------------------------------------

/** Replaces `{key}` segments in a URL template with values from `params`. */
export function replacePathParams(
  path: string,
  params?: Record<string, string>,
): string {
  if (!params) return path
  return path.replace(/\{([^{}]+)\}/g, (_, key) => {
    const value = params[key]
    if (value === undefined) {
      throw new Error(`Missing path param: ${key}`)
    }
    return encodeURIComponent(value)
  })
}

// ---------------------------------------------------------------------------
// Type utilities derived from an openapi-typescript-generated Paths type
// ---------------------------------------------------------------------------

type SuccessResponseBody<
  Responses,
  Code extends keyof Responses,
> = 'content' extends keyof Responses[Code]
  ? 'application/json' extends keyof Responses[Code]['content']
    ? Responses[Code]['content']['application/json']
    : never
  : never

type PathResponseBody<
  Paths,
  Path extends keyof Paths,
  Method extends keyof Paths[Path],
> = 'responses' extends keyof Paths[Path][Method]
  ? 200 extends keyof Paths[Path][Method]['responses']
    ? SuccessResponseBody<Paths[Path][Method]['responses'], 200>
    : 201 extends keyof Paths[Path][Method]['responses']
      ? SuccessResponseBody<Paths[Path][Method]['responses'], 201>
      : 202 extends keyof Paths[Path][Method]['responses']
        ? SuccessResponseBody<Paths[Path][Method]['responses'], 202>
        : 207 extends keyof Paths[Path][Method]['responses']
          ? SuccessResponseBody<Paths[Path][Method]['responses'], 207>
          : never
  : never

type PathRequestBody<
  Paths,
  Path extends keyof Paths,
  Method extends keyof Paths[Path],
> = 'requestBody' extends keyof Paths[Path][Method]
  ? 'content' extends keyof Paths[Path][Method]['requestBody']
    ? 'application/json' extends keyof Paths[Path][Method]['requestBody']['content']
      ? Paths[Path][Method]['requestBody']['content']['application/json']
      : unknown
    : unknown
  : unknown

type PathQueryParams<
  Paths,
  Path extends keyof Paths,
  Method extends keyof Paths[Path],
> = 'parameters' extends keyof Paths[Path][Method]
  ? 'query' extends keyof Paths[Path][Method]['parameters']
    ? Paths[Path][Method]['parameters']['query']
    : Record<string, unknown>
  : Record<string, unknown>

/** Returns `true` (the literal type) when a method exists on a path. */
type HasMethod<
  Paths,
  Path extends keyof Paths,
  Method extends string,
> = Method extends keyof Paths[Path] ? true : false

// ---------------------------------------------------------------------------
// The factory
// ---------------------------------------------------------------------------

/**
 * Creates a typed route-caller bound to a given `HttpTransport`.
 *
 * Usage:
 * ```ts
 * const client = createCxApiClient<MyPaths>(transport)
 * const { get } = client('/some/path')()
 * const response = await get({ query: { page: 1 } })
 * ```
 *
 * Note: `Paths` is constrained to `object` rather than `Record<string, unknown>`
 * because `openapi-typescript` emits `interface paths`, and interfaces have no
 * implicit index signature — they cannot satisfy a `Record` constraint.
 *
 * Note: `openapi-typescript` generates `interface paths` (type-only), so the
 * method guards here are compile-time only. TypeScript enforces that callers
 * only access methods present in the spec; the runtime object shape matches
 * the type-level shape as long as callers use typed access (not `any` / bracket).
 */
export function createCxApiClient<Paths extends object>(
  transport: HttpTransport,
) {
  return function <Path extends keyof Paths & string>(path: Path) {
    return function (routeParams?: Record<string, string>) {
      const url = replacePathParams(path, routeParams)

      type GetResponse = 'get' extends keyof Paths[Path]
        ? PathResponseBody<Paths, Path, Extract<keyof Paths[Path], 'get'>>
        : never
      type PostResponse = 'post' extends keyof Paths[Path]
        ? PathResponseBody<Paths, Path, Extract<keyof Paths[Path], 'post'>>
        : never
      type PutResponse = 'put' extends keyof Paths[Path]
        ? PathResponseBody<Paths, Path, Extract<keyof Paths[Path], 'put'>>
        : never
      type PatchResponse = 'patch' extends keyof Paths[Path]
        ? PathResponseBody<Paths, Path, Extract<keyof Paths[Path], 'patch'>>
        : never
      type DeleteResponse = 'delete' extends keyof Paths[Path]
        ? PathResponseBody<Paths, Path, Extract<keyof Paths[Path], 'delete'>>
        : never

      type PostBody = 'post' extends keyof Paths[Path]
        ? PathRequestBody<Paths, Path, Extract<keyof Paths[Path], 'post'>>
        : unknown
      type PutBody = 'put' extends keyof Paths[Path]
        ? PathRequestBody<Paths, Path, Extract<keyof Paths[Path], 'put'>>
        : unknown
      type PatchBody = 'patch' extends keyof Paths[Path]
        ? PathRequestBody<Paths, Path, Extract<keyof Paths[Path], 'patch'>>
        : unknown

      type GetQuery = 'get' extends keyof Paths[Path]
        ? PathQueryParams<Paths, Path, Extract<keyof Paths[Path], 'get'>>
        : Record<string, unknown>
      type DeleteQuery = 'delete' extends keyof Paths[Path]
        ? PathQueryParams<Paths, Path, Extract<keyof Paths[Path], 'delete'>>
        : Record<string, unknown>

      return {
        ...((true as HasMethod<Paths, Path, 'get'>)
          ? {
              get: (
                query?: GetQuery,
              ): Promise<TransportResponse<GetResponse>> =>
                transport.get<GetResponse>(
                  url,
                  query as Record<string, unknown> | undefined,
                ),
            }
          : {}),

        ...((true as HasMethod<Paths, Path, 'post'>)
          ? {
              post: (
                body?: PostBody,
              ): Promise<TransportResponse<PostResponse>> =>
                transport.post<PostResponse>(url, body),
            }
          : {}),

        ...((true as HasMethod<Paths, Path, 'put'>)
          ? {
              put: (body?: PutBody): Promise<TransportResponse<PutResponse>> =>
                transport.put<PutResponse>(url, body),
            }
          : {}),

        ...((true as HasMethod<Paths, Path, 'patch'>)
          ? {
              patch: (
                body?: PatchBody,
              ): Promise<TransportResponse<PatchResponse>> =>
                transport.patch<PatchResponse>(url, body),
            }
          : {}),

        ...((true as HasMethod<Paths, Path, 'delete'>)
          ? {
              delete: (
                query?: DeleteQuery,
              ): Promise<TransportResponse<DeleteResponse>> =>
                transport.delete<DeleteResponse>(
                  url,
                  query as Record<string, unknown> | undefined,
                ),
            }
          : {}),
      }
    }
  }
}
